import { useState, useCallback } from "react";
import {
  Building2,
  Paintbrush,
  Sofa,
  Armchair,
  Lightbulb,
  Wifi,
  LucideIcon,
} from "lucide-react";
import { componentCategories as defaultCategories, SubCategory, MainCategory } from "@/data/componentCategories";

const STORAGE_KEY = "minzo-component-categories";

export interface EditableMainCategory {
  id: string;
  name: string;
  icon: LucideIcon;
  subCategories: SubCategory[];
}

// Icons mapped by default category id for restoration
const iconMap: Record<string, LucideIcon> = {
  "architectural-systems": Building2,
  "interior-hard-finishes": Paintbrush,
  "interior-soft-finishes": Sofa,
  "furniture-custom": Armchair,
  "lighting": Lightbulb,
  "systems-outdoor": Wifi,
};

interface StoredCategory {
  id: string;
  name: string;
  subCategories: SubCategory[];
}

function loadCategories(): EditableMainCategory[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: StoredCategory[] = JSON.parse(stored);
      return parsed.map((cat) => ({
        ...cat,
        icon: iconMap[cat.id] || Building2,
      }));
    }
  } catch {
    // ignore
  }
  return defaultCategories.map((c) => ({ ...c }));
}

function saveCategories(cats: EditableMainCategory[]) {
  const toStore: StoredCategory[] = cats.map(({ id, name, subCategories }) => ({
    id,
    name,
    subCategories,
  }));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
}

export function useEditableCategories() {
  const [categories, setCategories] = useState<EditableMainCategory[]>(loadCategories);

  const updateCategory = useCallback((catId: string, newName: string) => {
    setCategories((prev) => {
      const next = prev.map((c) => (c.id === catId ? { ...c, name: newName } : c));
      saveCategories(next);
      return next;
    });
  }, []);

  const updateSubCategory = useCallback((catId: string, subId: string, newName: string) => {
    setCategories((prev) => {
      const next = prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              subCategories: c.subCategories.map((s) =>
                s.id === subId ? { ...s, name: newName } : s
              ),
            }
          : c
      );
      saveCategories(next);
      return next;
    });
  }, []);

  const addSubCategory = useCallback((catId: string, name: string) => {
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    if (!id) return;
    setCategories((prev) => {
      const next = prev.map((c) =>
        c.id === catId
          ? { ...c, subCategories: [...c.subCategories, { id, name }] }
          : c
      );
      saveCategories(next);
      return next;
    });
  }, []);

  const removeSubCategory = useCallback((catId: string, subId: string) => {
    setCategories((prev) => {
      const next = prev.map((c) =>
        c.id === catId
          ? { ...c, subCategories: c.subCategories.filter((s) => s.id !== subId) }
          : c
      );
      saveCategories(next);
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCategories(defaultCategories.map((c) => ({ ...c })));
  }, []);

  const getCategoryName = useCallback(
    (id: string) => categories.find((c) => c.id === id)?.name || id,
    [categories]
  );

  return {
    categories,
    updateCategory,
    updateSubCategory,
    addSubCategory,
    removeSubCategory,
    resetToDefaults,
    getCategoryName,
  };
}
